import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Plus, Search, Cpu, Eye, ExternalLink, BatteryCharging, Signal, Gauge,
  Wallet, Power, Home, Clock,
} from "lucide-react";
import api, { formatDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import StatusPill from "@/components/StatusPill";
import CountryPhoneInput from "@/components/CountryPhoneInput";

const emptyForm = { serial_number: "", imei: "", sim_number: "" };

export default function Meters() {
  const nav = useNavigate();
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [deviceType, setDeviceType] = useState("all");
  const [areaFilter, setAreaFilter] = useState("all");
  const [areas, setAreas] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  // View dialog
  const [viewOpen, setViewOpen] = useState(false);
  const [viewData, setViewData] = useState(null);   // realtime payload
  const [viewLoading, setViewLoading] = useState(false);
  const [viewCfg, setViewCfg] = useState(null);

  const load = async () => {
    const params = { page, page_size: 20 };
    if (search) params.search = search;
    if (areaFilter && areaFilter !== "all") params.area_id = areaFilter;
    if (deviceType && deviceType !== "all") params.device_type = deviceType;
    const { data } = await api.get("/meters", { params });
    setRows(data.data || []); setTotal(data.total || 0);
  };
  const loadRefs = async () => {
    const [a] = await Promise.all([api.get("/areas")]);
    setAreas(a.data.data || []);
  };
  useEffect(() => { loadRefs(); }, []);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [page, search, deviceType, areaFilter]);

  const openCreate = () => { setForm(emptyForm); setOpen(true); };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.serial_number.trim()) { toast.error("Meter number is required"); return; }
    if (!form.imei.trim() || form.imei.trim().length < 14) { toast.error("Valid IMEI required (15 digits)"); return; }
    setSaving(true);
    try {
      await api.post("/meters", {
        device_type: "UDP",
        serial_number: form.serial_number.trim(),
        imei: form.imei.trim(),
        sim_number: form.sim_number || null,
      });
      toast.success("Meter registered"); setOpen(false); load();
    } catch (err) {
      toast.error(formatDetail(err.response?.data?.detail) || "Failed to register meter");
    } finally { setSaving(false); }
  };

  const openView = async (m) => {
    setViewOpen(true); setViewLoading(true); setViewData(null);
    try {
      const [rt, cfg] = await Promise.all([
        api.get(`/meters/${m.id}/realtime`),
        api.get("/config"),
      ]);
      setViewData(rt.data);
      setViewCfg(cfg.data.data || null);
    } catch (err) {
      toast.error(formatDetail(err.response?.data?.detail) || "Failed to load meter");
      setViewOpen(false);
    } finally { setViewLoading(false); }
  };

  const isOccupied = (m) => Boolean(m.customer_name || m.household_id);

  return (
    <div className="space-y-6" data-testid="meters-page">
      <div className="flex items-start justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-slate-500 font-semibold">Device Fleet</div>
          <h1 className="font-heading text-3xl font-bold text-slate-900 tracking-tight mt-1">Meters</h1>
          <p className="text-sm text-slate-500 mt-1">Manage smart meters, valve state and downlink commands.</p>
        </div>
        <Button onClick={openCreate} data-testid="meter-add-button"
          className="bg-brand-600 hover:bg-brand-700 rounded-sm">
          <Plus size={16} className="mr-1" /> Add meter
        </Button>
      </div>

      <div className="bg-white border border-slate-200 rounded-sm">
        <div className="p-4 border-b border-slate-200 flex flex-col md:flex-row md:items-center gap-3">
          <div className="relative flex-1 max-w-sm">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input placeholder="Search deveui / IMEI / SN / customer…"
              className="pl-9 rounded-sm"
              value={search} onChange={(e) => { setPage(1); setSearch(e.target.value); }}
              data-testid="meter-search-input" />
          </div>
          <Select value={areaFilter} onValueChange={(v) => { setPage(1); setAreaFilter(v); }}>
            <SelectTrigger className="w-52 rounded-sm" data-testid="meter-area-filter"><SelectValue placeholder="All areas" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All areas</SelectItem>
              {areas.map((a) => (<SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>))}
            </SelectContent>
          </Select>
          <div className="ml-auto text-xs text-slate-500 tabular-nums">{total} total</div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-[11px] uppercase tracking-wider">
              <tr>
                <th className="text-left px-4 py-2 font-semibold">Meter number</th>
                <th className="text-left px-4 py-2 font-semibold">IMEI</th>
                <th className="text-left px-4 py-2 font-semibold">Valve status</th>
                <th className="text-left px-4 py-2 font-semibold">Occupied</th>
                <th className="text-right px-4 py-2 font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody data-testid="meters-table-body">
              {rows.length === 0 && (
                <tr><td colSpan={5} className="p-8 text-center text-sm text-slate-400">No meters registered yet.</td></tr>
              )}
              {rows.map((m) => (
                <tr key={m.id}
                  className="border-t border-slate-100 hover:bg-slate-50"
                  data-testid={`meter-row-${m.id}`}>
                  <td className="px-4 py-2">
                    <div className="flex items-center gap-2">
                      <div className="h-6 w-6 rounded-sm bg-slate-100 flex items-center justify-center text-slate-500"><Cpu size={12} /></div>
                      <div className="font-medium text-slate-800 font-mono text-[13px]">{m.serial_number || "—"}</div>
                    </div>
                  </td>
                  <td className="px-4 py-2 font-mono text-[13px] text-slate-700 tabular-nums">{m.imei || "—"}</td>
                  <td className="px-4 py-2"><StatusPill value={m.valve_state} /></td>
                  <td className="px-4 py-2">
                    <span
                      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-sm text-[11px] font-semibold border ${
                        isOccupied(m)
                          ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                          : "bg-slate-100 text-slate-600 border-slate-200"
                      }`}
                      data-testid={`meter-occupied-${m.id}`}
                    >
                      {isOccupied(m) ? "Yes" : "No"}
                    </span>
                    {isOccupied(m) && m.customer_name && (
                      <div className="text-[11px] text-slate-500 mt-1">{m.customer_name}</div>
                    )}
                  </td>
                  <td className="px-4 py-2 text-right">
                    <div className="inline-flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => openView(m)}
                        data-testid={`meter-view-${m.id}`}
                        className="rounded-sm h-7 px-2"
                      >
                        <Eye size={13} className="mr-1" /> View
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => nav(`/meters/${m.id}`)}
                        data-testid={`meter-open-${m.id}`}
                        className="rounded-sm h-7 px-2 text-slate-500 hover:text-slate-900"
                        title="Open full detail page"
                      >
                        <ExternalLink size={13} />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {total > 20 && (
          <div className="flex items-center justify-end gap-2 p-3 border-t border-slate-200 text-sm">
            <Button variant="outline" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}
              className="rounded-sm">Prev</Button>
            <div className="tabular-nums text-slate-600">Page {page} / {Math.ceil(total / 20)}</div>
            <Button variant="outline" disabled={page >= Math.ceil(total / 20)} onClick={() => setPage((p) => p + 1)}
              className="rounded-sm">Next</Button>
          </div>
        )}
      </div>

      {/* --------- Add-meter dialog --------- */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md rounded-sm" data-testid="meter-form-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Add meter</DialogTitle>
            <DialogDescription>Register a new smart LPG meter on the platform.</DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-600 font-semibold">Meter number</Label>
              <Input required value={form.serial_number}
                onChange={(e) => setForm({ ...form, serial_number: e.target.value })}
                data-testid="meter-serial-input" className="mt-1 rounded-sm font-mono"
                placeholder="e.g. GSF-2026-000123" autoFocus />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-600 font-semibold">IMEI</Label>
              <Input required value={form.imei}
                onChange={(e) => setForm({ ...form, imei: e.target.value.replace(/\D/g, "").slice(0, 15) })}
                data-testid="meter-imei-input" className="mt-1 rounded-sm font-mono tabular-nums"
                placeholder="15-digit device IMEI" inputMode="numeric" />
              <div className="text-[10px] text-slate-400 mt-1">From the meter&apos;s modem — usually printed on the label.</div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-600 font-semibold">SIM card number</Label>
              <div className="mt-1">
                <CountryPhoneInput value={form.sim_number}
                  onChange={(v) => setForm({ ...form, sim_number: v })}
                  testId="meter-sim" placeholder="MSISDN on the SIM" />
              </div>
              <div className="text-[10px] text-slate-400 mt-1">The mobile number assigned to the SIM inside the meter.</div>
            </div>
            <DialogFooter className="pt-2">
              <Button type="button" variant="outline" onClick={() => setOpen(false)} className="rounded-sm" data-testid="meter-form-cancel">Cancel</Button>
              <Button type="submit" disabled={saving} data-testid="meter-form-submit"
                className="bg-brand-600 hover:bg-brand-700 rounded-sm">
                {saving ? "Adding…" : "Add meter"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* --------- View meter details dialog --------- */}
      <Dialog open={viewOpen} onOpenChange={setViewOpen}>
        <DialogContent className="max-w-2xl rounded-sm" data-testid="meter-view-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Cpu size={16} className="text-slate-500" /> Meter overview
            </DialogTitle>
            <DialogDescription>
              Snapshot of the selected meter — for full history use &quot;Open in full page&quot;.
            </DialogDescription>
          </DialogHeader>
          {viewLoading && <div className="py-12 text-center text-sm text-slate-400">Loading…</div>}
          {viewData && <MeterViewBody data={viewData} cfg={viewCfg} onOpenFull={(id) => { setViewOpen(false); nav(`/meters/${id}`); }} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// -------- View dialog body ---------------------------------------------------
function Line({ icon: Icon, label, value, sub, testId }) {
  return (
    <div className="flex items-start gap-3" data-testid={testId}>
      <div className="h-8 w-8 shrink-0 rounded-sm bg-slate-100 grid place-items-center text-slate-500">
        <Icon size={14} strokeWidth={1.9} />
      </div>
      <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{label}</div>
        <div className="text-sm font-semibold text-slate-900 tabular-nums truncate">{value}</div>
        {sub && <div className="text-[10px] text-slate-500 tabular-nums">{sub}</div>}
      </div>
    </div>
  );
}

function MeterViewBody({ data, cfg, onOpenFull }) {
  const m = data.meter;
  const r = data.last_reading;
  const occupied = Boolean(m.customer_name || m.household_id);
  const currency = cfg?.currency || "UGX";
  const price = Number(m.price_per_m3 || 0);
  const remainingValue = (m.remaining_flow_m3 || 0) * price;

  return (
    <div className="space-y-5">
      {/* Header row: identity + status */}
      <div className="flex items-center justify-between border-b border-slate-100 pb-3">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Meter number</div>
          <div className="font-heading text-lg font-bold text-slate-900 font-mono">{m.serial_number || "—"}</div>
          <div className="text-[11px] text-slate-500 font-mono tabular-nums mt-0.5">IMEI {m.imei || "—"}</div>
        </div>
        <div className="flex flex-col items-end gap-1">
          <span className={`px-2 py-0.5 rounded-sm text-[11px] font-semibold border ${
            occupied ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                     : "bg-slate-100 text-slate-600 border-slate-200"
          }`}>Occupied: {occupied ? "Yes" : "No"}</span>
          <StatusPill value={m.valve_state} />
        </div>
      </div>

      {/* Household */}
      <div className="bg-slate-50 border border-slate-200 rounded-sm p-3">
        <div className="flex items-center gap-3">
          <div className="h-8 w-8 rounded-sm bg-white border border-slate-200 grid place-items-center text-slate-500">
            <Home size={14} strokeWidth={1.9} />
          </div>
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Household</div>
            <div className="text-sm font-semibold text-slate-900 truncate" data-testid="view-household-name">
              {m.customer_name || "— unassigned —"}
            </div>
            {m.customer_phone && (
              <div className="text-[11px] text-slate-500 tabular-nums">+{m.customer_phone}</div>
            )}
          </div>
        </div>
      </div>

      {/* Metrics grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <Line
          icon={Gauge}
          label="Cumulative reading"
          value={`${(m.reading_m3 || 0).toFixed(3)} m³`}
          testId="view-reading"
        />
        <Line
          icon={Gauge}
          label="Remaining"
          value={`${(m.remaining_flow_m3 || 0).toFixed(3)} m³`}
          sub={price > 0 ? `≈ ${currency} ${remainingValue.toLocaleString(undefined, { maximumFractionDigits: 2 })}` : null}
          testId="view-remaining"
        />
        <Line
          icon={Wallet}
          label="Balance"
          value={`${currency} ${(m.balance || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`}
          testId="view-balance"
        />
        <Line
          icon={BatteryCharging}
          label="Battery"
          value={m.battery_voltage != null ? `${Number(m.battery_voltage).toFixed(2)} V` : "—"}
          sub={m.battery_voltage != null ? (Number(m.battery_voltage) < 3.6 ? "low — replace soon" : "healthy") : null}
          testId="view-battery"
        />
        <Line
          icon={Signal}
          label="Signal"
          value={`CSQ ${m.signal_rssi ?? "—"}`}
          sub={m.signal_snr != null ? `SNR ${m.signal_snr} dB` : null}
          testId="view-signal"
        />
        <Line
          icon={Power}
          label="Run state"
          value={m.run_state || "—"}
          testId="view-run-state"
        />
      </div>

      {/* Latest reading footer */}
      <div className="text-[11px] text-slate-500 border-t border-slate-100 pt-3 flex items-center gap-2">
        <Clock size={12} />
        <span data-testid="view-last-report">
          Last uplink: {r?.reported_at ? new Date(r.reported_at).toLocaleString()
            : m.last_report_at ? new Date(m.last_report_at).toLocaleString()
            : "no uplinks yet"}
        </span>
      </div>

      <DialogFooter className="pt-2">
        <Button
          variant="outline"
          onClick={() => onOpenFull(m.id)}
          data-testid="view-open-full"
          className="rounded-sm"
        >
          <ExternalLink size={13} className="mr-1" /> Open full detail
        </Button>
      </DialogFooter>
    </div>
  );
}
